import { Box, Grid, Divider } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useEffect, useState, useMemo } from "react";

import { TextField, Textarea, Autocomplete, Button } from "../../../../../../shared";
import { SideModal } from "../../../../../../components";
import { useManufacturerProduct, useProductCategory } from "../../../../../../hooks/query";
import manufacturerProductResolver from "../../../../../../libs/form-validations/manufacturer-product";

import type { IManufacturerProduct } from "../../../../../../interfaces";

interface ProductFormProps {
    manufacturerId: string;
    product?: IManufacturerProduct | null;
    onClose: () => void;
}

export default function ProductForm({ manufacturerId, product, onClose }: ProductFormProps) {
    const queryClient = useQueryClient();
    const { useCreate, useUpdate } = useManufacturerProduct();
    const { useList: useListCategories } = useProductCategory();
    const { data: categoriesData } = useListCategories();

    const createProductSvc = useCreate();
    const updateProductSvc = useUpdate();

    const isEditing = !!product;
    const categories = (categoriesData as any[]) || [];

    const categoryOptions = useMemo(
        () =>
            categories.map((cat) => ({
                value: cat.Id,
                label: cat.Name,
            })),
        [categories]
    );

    const defaultCategory = useMemo(() => {
        if (product?.Category_Id && categoryOptions.length > 0) {
            return (
                categoryOptions.find((opt: { value: string; label: string }) => opt.value === product.Category_Id) ||
                null
            );
        }
        return null;
    }, [product?.Category_Id, categoryOptions]);

    const methods = useForm({
        resolver: manufacturerProductResolver,
        defaultValues: {
            Name: product?.Name || "",
            Category_Id: product?.Category_Id || "",
            Category: product?.Category || "",
            Subcategory: product?.Subcategory || "",
            Color: product?.Color || "",
            Description: product?.Description || "",
            UnitType: product?.UnitType || "",
            UnitCoverage: product?.UnitCoverage || null,
            IsActive: typeof product?.IsActive === "number" ? product.IsActive === 1 : product?.IsActive ?? true,
        },
    });

    // Track the selected category object separately
    const [selectedCategory, setSelectedCategory] = useState<{ value: string; label: string } | null>(null);

    // Update selected category when product or categories load
    useEffect(() => {
        if (defaultCategory) {
            setSelectedCategory(defaultCategory);
        } else {
            setSelectedCategory(null);
        }
    }, [defaultCategory]);

    const onSubmit = methods.handleSubmit(async (data) => {
        try {
            if (isEditing && product) {
                await updateProductSvc.mutateAsync({
                    Id: product.Id,
                    Manufacturers_Id: manufacturerId,
                    Name: data.Name,
                    Category_Id: data.Category_Id || undefined,
                    Category: data.Category || undefined,
                    Subcategory: data.Subcategory || undefined,
                    Color: data.Color || undefined,
                    Description: data.Description || undefined,
                    UnitType: data.UnitType || undefined,
                    UnitCoverage: data.UnitCoverage || undefined,
                    IsActive: data.IsActive ?? true,
                });
            } else {
                await createProductSvc.mutateAsync({
                    Manufacturers_Id: manufacturerId,
                    Name: data.Name,
                    Category_Id: data.Category_Id || undefined,
                    Category: data.Category || undefined,
                    Subcategory: data.Subcategory || undefined,
                    Color: data.Color || undefined,
                    Description: data.Description || undefined,
                    UnitType: data.UnitType || undefined,
                    UnitCoverage: data.UnitCoverage || undefined,
                    IsActive: data.IsActive ?? true,
                });
            }

            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ["manufacturer-products", manufacturerId] });
            onClose();
        } catch (error) {
            console.error("Failed to save product:", error);
        }
    });

    const isLoading = createProductSvc.isPending || updateProductSvc.isPending;
    const formRef = useRef<HTMLFormElement>(null);

    const handleSubmitClick = () => {
        if (formRef.current) {
            formRef.current.requestSubmit();
        }
    };

    return (
        <SideModal
            open={true}
            onClose={onClose}
            title={isEditing ? "Edit Product" : "Add Product"}
            description={isEditing ? "Update product details" : "Enter product details"}
            showAdditionalHeadersIcons={false}
        >
            <FormProvider {...methods}>
                <form ref={formRef} onSubmit={onSubmit}>
                    <Box p={3} display="flex" flexDirection="column" gap={2}>
                        <TextField
                            name="Name"
                            label="Product Name"
                            helperText="Enter the product name"
                            placeholder="e.g., Tamko Heritage"
                        />

                        <Autocomplete
                            name="Category_Id"
                            label="Category"
                            helperText="Select a product category"
                            placeholder="Select category..."
                            options={categoryOptions}
                            value={selectedCategory}
                            getOptionLabel={(option: any) => {
                                if (typeof option === "string") return option;
                                return option?.label || option || "";
                            }}
                            isOptionEqualToValue={(option: any, value: any) => {
                                if (!option || !value) return false;
                                if (typeof option === "string" || typeof value === "string") return false;
                                return option.value === value?.value;
                            }}
                            onChange={(e: any, value: any) => {
                                setSelectedCategory(value);
                                methods.setValue("Category_Id", value?.value || "", { shouldDirty: true });
                            }}
                        />

                        <TextField
                            name="Subcategory"
                            label="Subcategory"
                            helperText="Product subcategory or base product name"
                            placeholder="e.g., Tamko Heritage"
                        />

                        <Grid container spacing={2}>
                            <Grid xs={6}>
                                <TextField
                                    name="Color"
                                    label="Color"
                                    helperText="Product color variant"
                                    placeholder="e.g., Red, Green"
                                />
                            </Grid>
                            <Grid xs={6}>
                                <TextField
                                    name="UnitType"
                                    label="Unit Type"
                                    helperText="Unit of measurement"
                                    placeholder="e.g., SQ, LF, Count"
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            name="UnitCoverage"
                            label="Unit Coverage"
                            helperText="Coverage per unit"
                            placeholder="e.g., 100"
                            type="number"
                        />

                        <Textarea
                            name="Description"
                            label="Description"
                            helperText="Optional product description"
                            placeholder="Enter product description..."
                            minRows={3}
                        />

                        <Divider sx={{ my: 1 }} />

                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button variant="outlined" color="neutral" onClick={onClose} disabled={isLoading}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading || !methods.formState.isDirty}
                                loading={isLoading}
                                variant="soft"
                                color="primary"
                            >
                                {isEditing ? "Update Product" : "Create Product"}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </FormProvider>
        </SideModal>
    );
}
